import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Download,
  Visibility,
  Description,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  Folder,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import documentsService, { Document } from '../services/documentsService';



const Documents: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch documents from API
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentsService.getAll({ search: searchTerm });
      // Ensure data is an array
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to fetch documents');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        fetchDocuments();
      } else {
        fetchDocuments();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      await documentsService.delete(selectedDocument.id);
      setDocuments(prev => prev.filter(item => item.id !== selectedDocument.id));
      setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete document', severity: 'error' });
      console.error('Error deleting document:', err);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const blob = await documentsService.download(document.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Download started', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to download document', severity: 'error' });
      console.error('Error downloading document:', err);
    }
  };

  const handlePreview = async (document: Document) => {
    try {
      const previewUrl = await documentsService.getPreviewUrl(document.id);
      window.open(previewUrl, '_blank');
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to open preview', severity: 'error' });
      console.error('Error opening preview:', err);
    }
  };

  const handleStatusChange = async (document: Document, newStatus: Document['status']) => {
    try {
      const updated = await documentsService.updateStatus(document.id, newStatus);
      setDocuments(prev => prev.map(item => item.id === document.id ? updated : item));
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
      console.error('Error updating status:', err);
    }
  };

  const getTypeColor = (type: Document['type']) => {
    switch (type) {
      case 'contract':
        return 'primary';
      case 'blueprint':
        return 'info';
      case 'permit':
        return 'warning';
      case 'invoice':
        return 'success';
      case 'report':
        return 'secondary';
      case 'photo':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <PictureAsPdf color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image color="primary" />;
      case 'zip':
      case 'rar':
        return <Folder color="warning" />;
      default:
        return <InsertDriveFile color="action" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Ensure documents is an array before using array methods
  const documentsArray = Array.isArray(documents) ? documents : [];
  
  const filteredDocuments = documentsArray.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.project && item.project.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalSize = documentsArray.reduce((sum, item) => sum + (item.size || 0), 0);
  const activeCount = documentsArray.filter(item => item.status === 'active').length;
  const pendingCount = documentsArray.filter(item => item.status === 'pending-review').length;
  const archivedCount = documentsArray.filter(item => item.status === 'archived').length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading documents...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Document Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/documents/upload')}
        >
          Upload Document
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Documents
            </Typography>
            <Typography variant="h5" component="div">
              {documentsArray.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Active
            </Typography>
            <Typography variant="h5" component="div" color="success.main">
              {activeCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending Review
            </Typography>
            <Typography variant="h5" component="div" color="warning.main">
              {pendingCount}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Size
            </Typography>
            <Typography variant="h5" component="div">
              {formatFileSize(totalSize)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Documents Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocuments.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getFileIcon(item.fileType)}
                    <Box>
                      <Typography variant="subtitle2">{item.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.fileType} • {formatFileSize(item.size)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.type.replace('-', ' ').toUpperCase()}
                    color={getTypeColor(item.type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{item.project || '-'}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{formatFileSize(item.size)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {item.uploadedBy.charAt(0)}
                    </Avatar>
                    {item.uploadedBy}
                  </Box>
                </TableCell>
                <TableCell>{new Date(item.uploadDate).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip
                    label={item.status.replace('-', ' ').toUpperCase()}
                    color={item.status === 'active' ? 'success' : item.status === 'pending-review' ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>v{item.version}</TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    title="View"
                    onClick={() => handlePreview(item)}
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Download"
                    onClick={() => handleDownload(item)}
                  >
                    <Download />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/documents/${item.id}`)}
                    title="Edit"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    title="Delete"
                    onClick={() => {
                      setSelectedDocument(item);
                      setDeleteDialogOpen(true);
                      // Blur currently focused element to avoid aria-hidden focus conflict
                      requestAnimationFrame(() => {
                        const ae = document.activeElement as HTMLElement | null;
                        ae?.blur?.();
                      });
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredDocuments.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No documents found
          </Typography>
          <Typography color="textSecondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Start by uploading your first document'}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedDocument?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Error Display */}
      {error && (
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Documents;